import React, { type ReactElement } from 'react';
import { Box, Icon, Typography } from '@saviynt/design-system';
import cx from 'classnames';

import { CF_SEC_BLD } from '../../constants';

import './SodFunction.css';

/**
 * Interface representing a function item in the SodFunction component
 * @interface Functions
 * @property {string | number} id - Unique identifier for the function
 * @property {string} text - Display text for the function
 */
interface Functions {
  id?: string | number;
  text?: string;
}

/**
 * Props interface for the SodFunction component
 * @interface SodFunctionProps
 * @property {string} [className] - Optional CSS class name for additional styling
 * @property {Functions[]} functions - Array of function items to display
 */
interface SodFunctionProps {
  className?: string;
  functions: Functions[];
}

/**
 * SodFunction component displays a list of functions with icons in a vertical layout
 *
 * @component
 * @example
 * const functions = [
 *   { id: 1, text: 'Function 1' },
 *   { id: 2, text: 'Function 2' }
 * ];
 *
 * return (
 *   <SodFunction
 *     functions={functions}
 *     className="custom-class"
 *   />
 * )
 *
 * @param {SodFunctionProps} props - The component props
 * @param {string} [props.className] - Optional CSS class name for additional styling
 * @param {Functions[]} props.functions - Array of function items to display
 * @returns {ReactElement} A React element representing the SodFunction component
 */
function SodFunction({
  className,
  functions = [] as Functions[],
}: SodFunctionProps): ReactElement {
  const wrapperClasses = cx('SodFunction', className);

  return (
    <Box tag='div' className={wrapperClasses}>
      {functions.map((func, index) => {
        const isFirst = Boolean(index === 0);
        const isLast = Boolean(index === functions.length - 1);

        const itemClasses = cx(
          'SodFunction-wrapper',
          isFirst && 'SodFunction-wrapper--isFirst',
          isLast && 'SodFunction-wrapper--isLast'
        );

        return (
          <Box key={func.id} className={itemClasses}>
            <Icon kind='JobFunction' className='SodFunction-icon' />
            <Typography kind='body2-bold' color={CF_SEC_BLD}>
              {func.text}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}

/**
 * Default props for the SodFunction component
 * @type {Partial<SodFunctionProps>}
 */
SodFunction.defaultProps = {
  className: '',
};

export default SodFunction;
